/**
 * ═══ WHAT KIND OF ATTEMPT WAS THIS? ═══════════════════════════════════════════════════════════
 *
 * `unique` asks how many DIFFERENT things a student has tried, and that question is unanswerable
 * from raw values. A student nudging a slider from 4.90 to 4.91 to 4.92 has not had three ideas;
 * a student answering 20, then 200, then 2, has had three. Counting distinct raw answers says
 * both are three, which makes "after 3 unique attempts, offer a hint" fire on the first student
 * and the second alike.
 *
 * ## The rule, once, instead of a table
 *
 * Everything ranged buckets by THE BLOCK'S OWN TOLERANCE. The teacher has already declared what
 * counts as the same answer -- that is what a tolerance IS -- so the signature reuses that
 * declaration rather than inventing a second notion of closeness per block type.
 *
 * **Bucket 0 is the accepted band itself.** Tiling is anchored on the answer, not on zero: with a
 * tiling anchored at zero the accepted band could straddle a boundary, and two correct answers
 * either side of it would count as two different attempts.
 *
 * **Asymmetric tolerance gives two half-tilings** that meet at the answer, wide on one side and
 * narrow on the other. That falls out of measuring each side by its own half-width rather than
 * being a special case.
 *
 * **Percentages need no handling here.** A percentage tolerance is resolved against the answer
 * when the band is computed, so by the time it arrives it is an ordinary interval.
 *
 * ## Where it does not apply
 *
 * A zero-width band -- tolerance 0, or none authored -- cannot be tiled: every value would be its
 * own bucket, which is the raw-value-equality failure this exists to avoid. Those fall back to
 * exact equality, which is the honest answer for a block that accepts exactly one number.
 *
 * Discrete blocks never come here at all. A selection, a sequence and a normalised string are
 * already their own signatures.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/** The answer a band is tiled around. */
function anchorOf(band) {
    return band.at !== undefined && Number.isFinite(band.at) ? band.at : (band.lo + band.hi) / 2;
}
/** Degrees in a full turn, for the circular blocks. */
const TURN = 360;
/**
 * Which bucket a value falls in, relative to ONE band. 0 is the band itself.
 *
 * `null` when the band has no width to tile by, which is the caller's signal to fall back to
 * exact equality rather than to invent a bucket.
 */
export function bucketIn(value, band) {
    if (!Number.isFinite(value) || !Number.isFinite(band.lo) || !Number.isFinite(band.hi))
        return null;
    const anchor = anchorOf(band);
    // Each side measured by ITS OWN half-width, which is the whole of asymmetric support.
    const half = value > anchor ? band.hi - anchor : anchor - band.lo;
    // Checked BEFORE the inclusive test, not after: a zero-width band has to report "cannot tile"
    // even for a value that hits the answer exactly, or the exact hit would be bucket 0 while
    // every miss fell back to exact equality -- two different signature schemes in one block.
    if (half <= 0)
        return null;
    // Inclusive: a value sitting exactly on an endpoint is accepted by the grader, so it has to be
    // bucket 0 here too, and the formula below would put it half a tile out.
    if (value >= band.lo && value <= band.hi)
        return 0;
    // Boundaries land in the FURTHER tile (`Math.round` goes up on a half), which is arbitrary but
    // has to be decided somewhere; what matters is that it is decided once, here.
    return Math.round((value - anchor) / (2 * half));
}
/**
 * THE INVERSE OF `bucketIn`: a representative value for a bucket.
 *
 * The forward direction answers "how far wrong was this attempt, in units of the teacher's own
 * tolerance". The inverse answers "show me a value that far wrong", which is what a sampler
 * needs — and it is the same arithmetic read backwards, so the two cannot disagree about what a
 * band-width is.
 *
 * Worth stating why that matters: a sampled wrong answer and the attempt counter now measure
 * distance the same way, so a value offered as a mistake is one the counter would class as a
 * genuinely different idea rather than a nudge. Those were independent notions of "how wrong"
 * until this existed, and nothing would have caught them drifting.
 *
 * `0` returns the anchor — the answer itself, which is the one value bucket 0 is certainly made
 * of. `null` when the band cannot be tiled, exactly as `bucketIn` returns `null`.
 */
export function valueInBucket(band, bucket) {
    if (!Number.isFinite(band.lo) || !Number.isFinite(band.hi) || !Number.isFinite(bucket))
        return null;
    const anchor = anchorOf(band);
    // Each side by its own half-width, matching `bucketIn` — which is the whole of asymmetric
    // support in both directions.
    const half = bucket > 0 ? band.hi - anchor : anchor - band.lo;
    if (half <= 0)
        return null;
    return anchor + bucket * 2 * half;
}
/**
 * THE SPAN OF ONE BUCKET — every value that reads as that far wrong.
 *
 * `bucketIn` rounds, so bucket k is not a point but a tile: `(v - anchor) / (2 * half)` lands in
 * k for anything from `k - 0.5` to `k + 0.5`. Written out, that is `anchor + (2k-1)*half` to
 * `anchor + (2k+1)*half`, and bucket 0 comes out as exactly the accepted band — which is the
 * check that this is the same tiling and not a second one that happens to look similar.
 *
 * Exists so a sampler can DRAW inside a tier rather than always offering its one representative
 * value. "Another wrong answer, the same kind of wrong" is a different request from "a wrong
 * answer", and the second is all a single value per tier can answer.
 */
export function bucketSpan(band, bucket) {
    if (!Number.isFinite(band.lo) || !Number.isFinite(band.hi) || !Number.isFinite(bucket))
        return null;
    const anchor = anchorOf(band);
    // Each side by its own half-width, as everything here does. A tile straddling the anchor
    // cannot happen: bucket 0 is the band, and no other bucket touches it.
    const half = bucket > 0 ? band.hi - anchor : anchor - band.lo;
    if (half <= 0)
        return null;
    const lo = anchor + (2 * bucket - 1) * half;
    const hi = anchor + (2 * bucket + 1) * half;
    return lo <= hi ? { lo, hi } : { lo: hi, hi: lo };
}
/** How far outside its band a value is, in band-widths -- the measure multi-answer attribution
 *  uses to decide which answer a wrong value was aimed at. */
function distanceInBands(value, band) {
    const bucket = bucketIn(value, band);
    return bucket === null ? Number.POSITIVE_INFINITY : Math.abs(bucket);
}
/**
 * The signature for a value against every accepted band.
 *
 * **Multi-answer attribution is by BAND-WIDTHS, not raw distance.** Each accepted answer owns its
 * own tiling, and a wrong value is attributed to the answer it is fewest tolerances away from.
 * The consequence to know: where two answers have very different tolerances, a value can be
 * attributed to one that is further away in raw value -- which is the intended reading, because
 * the wider tolerance is the teacher saying that answer is the looser target.
 *
 * Ties go to the earlier band, so the signature cannot depend on iteration order.
 */
export function signatureForBands(value, bands) {
    if (!Number.isFinite(value))
        return 'x:nan';
    let best = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < bands.length; i += 1) {
        const d = distanceInBands(value, bands[i]);
        if (d < bestDistance) {
            bestDistance = d;
            best = i;
        }
    }
    // No band could be tiled (all zero-width, or none authored). Exact equality is the honest
    // fallback: a block accepting exactly one number has no notion of "nearly the same answer".
    if (best === -1)
        return `x:${value}`;
    return `b${best}:${bucketIn(value, bands[best])}`;
}
/**
 * The circular version, for a heading.
 *
 * **The antipodal bucket must be normalised**, and that is the only reason this is not the linear
 * function. Tiling outward from an answer on a circle meets itself on the far side, so 179 and
 * -181 are the same place and a signed bucket count would call two near-identical headings two
 * different attempts -- exactly at the point furthest from the answer, where a student is most
 * likely to be flailing and most likely to trip an attempt rule.
 */
export function signatureForHeading(heading, bands) {
    if (!Number.isFinite(heading))
        return 'x:nan';
    if (bands.length === 0)
        return `x:${heading}`;
    let best = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestBucket = 0;
    for (let i = 0; i < bands.length; i += 1) {
        const band = bands[i];
        const anchor = anchorOf(band);
        // Measured as a signed turn from the answer, so the tiling is anchored on the answer exactly
        // as the linear one is; the wrap is then folded out of the bucket, not out of the angle.
        const delta = ((heading - anchor + 540) % TURN) - 180;
        const bucket = bucketIn(anchor + delta, band);
        if (bucket === null)
            continue;
        const half = Math.max((band.hi - band.lo) / 2, Number.EPSILON);
        // The antipode is ONE place. Tiling outward from an answer on a circle meets itself on the
        // far side, so the outermost bucket either way is the same region -- and it is collapsed to
        // a single POSITIVE bucket, not merely clamped in magnitude, because +9 and -9 would
        // otherwise still read as two attempts at the one spot furthest from the answer.
        const maxBucket = Math.floor(180 / (2 * half));
        const folded = Math.abs(bucket) >= maxBucket ? maxBucket : bucket;
        const d = Math.abs(folded);
        if (d < bestDistance) {
            bestDistance = d;
            best = i;
            bestBucket = folded;
        }
    }
    if (best === -1)
        return `x:${heading}`;
    return `b${best}:${bestBucket}`;
}
/**
 * The radial version, for a position.
 *
 * **Metres from the target, bucketed by the tolerance radius -- never degrees.** A degree of
 * latitude is about 111km, so every attempt a student could physically make would land in one
 * bucket and `unique` would read 1 forever.
 *
 * One-sided by nature: there is no "below" a distance, so this tiles outward from the target
 * rather than either side of a centre. Bucket 0 is inside the radius, which is the accepted disc.
 */
export function signatureForDistance(metres, radius) {
    if (!Number.isFinite(metres))
        return 'x:nan';
    if (!Number.isFinite(radius) || radius <= 0)
        return `x:${metres}`;
    if (metres <= radius)
        return 'd:0';
    return `d:${Math.ceil(metres / radius) - 1}`;
}
//# sourceMappingURL=attempt-signature.js.map